import type { AppLocale } from "../i18n/types";
import type { RatingCategory, Vpn, VpnSortingConfig } from "../types";

// `website_accessible_from_ru` is overloaded on the model (see app/models.py):
// operators put either an availability status ("Да" / "Нет" / "Неизвестно") or
// a mirror URL in it. Only the URL form is rendered — availability itself is
// reported by `russia_context_awareness`, not by this field.
export const parseMirrorUrl = (raw?: string | null): string => {
  const value = (raw ?? "").trim();

  return /^https?:\/\//i.test(value) ? value : "";
};

// The "Ориентирован на РФ" badge reads the operator's flag straight: only an
// explicit `true` raises it, so an unset/absent value leaves the card bare
// rather than claiming something about a service nobody has checked yet.
export const isRuFocused = (russiaContextAwareness?: boolean): boolean =>
  russiaContextAwareness === true;

// "Прочие" is the section for services that failed the checks, so the
// "Ориентирован на РФ" badge there would read as a recommendation the section
// itself withholds — it is suppressed by category rather than by unticking the
// flag on every row.
// Category identity comes from the tier; the name match is the same legacy
// fallback MainPageVpns uses, and only applies while the tier is unset.
export const isOtherCategory = (rating: {
  tier?: string | null;
  name?: string;
}): boolean =>
  rating.tier ? rating.tier === "other" : rating.name === "Прочие VPN";

// Mirror of DEFAULT_* in app/views.py — used only when the payload predates
// `vpn_sorting`. The live values come from SiteConfig and are served by
// /api/site-config/, so an operator retuning the weights in the admin moves
// the merged section and the server-shuffled ones together.
export const DEFAULT_VPN_SORTING: VpnSortingConfig = {
  high_rating_coefficient: 3.0,
  secret_word_coefficient: 15.0,
  high_rating_threshold: 7,
  base_weight: 1.0,
  rating_weight_per_point: 0.5,
};

// The base fields are the Russian copy. *_en follows the English language,
// *_az the Azerbaijani language. Kazakhstan is only a region (there is no
// Kazakh language, and the region switch is often hidden). A blank copy
// falls back to Russian.
export const pickRegionalPromo = (
  locale: AppLocale,
  regionCode: string | undefined,
  ru?: string | null,
  kz?: string | null,
  az?: string | null,
  en?: string | null,
): string => {
  if (locale === "en") {
    return en || ru || "";
  }
  if (locale === "az") {
    return az || ru || "";
  }
  if (regionCode === "kz") {
    return kz || ru || "";
  }
  if (regionCode === "az") {
    return az || ru || "";
  }
  return ru || "";
};

// Port of calculate_vpn_weight (app/views.py). `rating` is the ranking weight,
// never the star (VPN_RATING.md). A secret word in *any* region counts, the
// same way the backend checks all three fields regardless of the site region.
export const calculateVpnWeight = (
  vpn: Vpn,
  config: VpnSortingConfig = DEFAULT_VPN_SORTING,
): number => {
  const rating = vpn.rating ?? 0;
  let weight = config.base_weight + rating * config.rating_weight_per_point;

  if (rating >= config.high_rating_threshold) {
    weight *= config.high_rating_coefficient;
  }

  if (
    vpn.secret_word ||
    vpn.secret_word_en ||
    vpn.secret_word_kz ||
    vpn.secret_word_az
  ) {
    weight *= config.secret_word_coefficient;
  }

  return weight;
};

// Port of weighted_shuffle (app/views.py): repeated weighted draw without
// replacement, so a heavier service is likelier to land high without ever
// being guaranteed a slot.
//
// The backend already shuffles every section; this exists for the merged
// silver+bronze list, which would otherwise be silver's draw followed by
// bronze's. Resampling the union from scratch discards the incoming order
// entirely, so the weights are not applied twice — the result is distributed
// exactly as if the union had been shuffled once, on the server.
export const weightedShuffle = (
  vpns: Vpn[],
  config: VpnSortingConfig = DEFAULT_VPN_SORTING,
): Vpn[] => {
  const pool = [...vpns];
  const result: Vpn[] = [];

  while (pool.length > 0) {
    const weights = pool.map((vpn) => calculateVpnWeight(vpn, config));
    const total = weights.reduce((sum, weight) => sum + weight, 0);

    // Falls back to index 0 — on an all-zero total (every coefficient tuned
    // down to nothing) this drains the pool in order instead of looping.
    let remaining = Math.random() * total;
    let selected = 0;

    for (let i = 0; i < weights.length; i += 1) {
      remaining -= weights[i];
      if (remaining <= 0) {
        selected = i;
        break;
      }
    }

    result.push(...pool.splice(selected, 1));
  }

  return result;
};

// Silver and bronze are one *public* section ("хорошие" VPN) even though they
// stay two RatingType rows in the admin — the model allows only one row per
// tier, and the tiers still drive the auto-rating. Silver carries the section
// identity (name, description, icon, order, nav_name) per the product call;
// the two lists of services are combined and reshuffled together, so bronze is
// mixed through the section rather than parked in its tail. They are still
// tellable apart inside the list — every card renders its own tier star.
//
// `shuffle` is injected so the merge itself stays deterministic and testable;
// callers pass the weighted shuffle bound to the operator's coefficients.
//
// Deliberately tolerant of missing rows: with no silver row bronze is left
// exactly as it was, so a half-configured admin loses no services. Nothing is
// reshuffled in that case either — a lone section keeps the server's draw.
export const mergeGoodTiers = (
  ratingList: RatingCategory[],
  shuffle: (vpns: Vpn[]) => Vpn[] = weightedShuffle,
): RatingCategory[] => {
  const silver = ratingList.find((rating) => rating.tier === "silver");
  const bronze = ratingList.find((rating) => rating.tier === "bronze");

  if (!silver || !bronze) {
    return ratingList;
  }

  return ratingList
    .filter((rating) => rating !== bronze)
    .map((rating) =>
      rating === silver
        ? {
            ...rating,
            vpns: shuffle([...(silver.vpns ?? []), ...(bronze.vpns ?? [])]),
          }
        : rating,
    );
};
